import { useEffect, useState } from "react";
import { WegetEventButtons } from "./wegets/weget-event-buttons-v2/weget-event-buttons";
import { WegetTabs } from "./wegets/weget-tabs";
import { WegetViewerPalletFirst } from "./wegets/weget-viewer-pallet-first";
import { WegetViewerItemFirst } from "./wegets/weget-viewer-item-first";
import { WegetStandardRules } from "./wegets/weget-standard-rules";
import { SellertoolSignatureUtils } from "../../../utils/sellertool-signature-utils";
import { useApiInfoContextValueHook } from "../../../contexts/ApiInfoContextProvider";
import axios from "axios";
import { customToast } from "../../../components/toast/custom-react-toastify/v1";
import { CustomBackdropController } from "../../../components/backdrop/default/v1";
import { WegetStageSelection } from "./wegets/weget-stage-selection";
import { useQueryParamsHook } from "../../../hooks/useQueryParamsHook";
import qs from 'qs';
import { ScrollToTopButton } from "../../../components/scroll-to-top-button";
import * as St from './index.styled';

const API_HOST = process.env.REACT_APP_MAIN_API_HOST;

export function MainView(props) {
    const queryParamsHook = useQueryParamsHook();
    const currentStageUUID = queryParamsHook?.params?.stage;

    const apiInfoContextValueHook = useApiInfoContextValueHook();

    const [currentStage, setCurrentStage] = useState(null);
    const [tab, setTab] = useState('LOAD')
    // 최상위 페이지: 'MAIN'(스테이지 적재 흐름) | 'STANDARD'(규격 적재 규칙, 스테이지 공유)
    const [page, setPage] = useState('MAIN');
    const [pallets, setPallets] = useState(null);
    const [loadItems, setLoadItems] = useState(null);
    // 확정 파레트 (별도 컬렉션 confirmed_pallets)
    const [confirmedPallets, setConfirmedPallets] = useState(null);
    const [confirmedLoadItems, setConfirmedLoadItems] = useState(null);

    useEffect(() => {
        if (currentStageUUID) {
            handleFetchCurrentStage();
        }
    }, [currentStageUUID]);

    useEffect(() => {
        if (!currentStage) {
            return;
        }

        handleReqFetch();
        handleReqFetchConfirmed();
    }, [currentStage]);

    const handleSetTab = (tab) => {
        setTab(tab);
    }

    const handleSetPallets = (datas) => {
        setPallets(datas);
    }

    const handleSetLoadItems = (datas) => {
        setLoadItems(datas);
    }

    const handleReqFetch = async () => {
        const timestamp = Date.now().toString();
        const signature = SellertoolSignatureUtils().generateSigniture({
            apiKey: apiInfoContextValueHook?.apiInfo?.apiKey,
            secretKey: apiInfoContextValueHook?.apiInfo?.secretKey,
            timestamp: timestamp
        })

        const headers = {
            'x-sellertool-apiKey': apiInfoContextValueHook?.apiInfo?.apiKey,
            'x-sellertool-timestamp': timestamp,
            'x-sellertool-signiture': signature
        }

        const params = {
            stageUUIDs: [currentStageUUID]
        }

        CustomBackdropController().showBackdrop();
        const fetchResult = await axios.get(`${API_HOST}/pallets`, {
            params: params,
            headers: headers,
            paramsSerializer: params => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            }
        })
            .then(res => {
                return {
                    res: res,
                    content: res.data?.content
                }
            })
            .catch(err => {
                customToast.error(err.response);
            })

        if (fetchResult?.content) {
            if (fetchResult?.content?.pallets?.length <= 0) {
                setPallets(null);
                setLoadItems(null);
            } else {
                setPallets(fetchResult?.content?.pallets);
                setLoadItems(fetchResult?.content?.loadItems);
            }
        }
        CustomBackdropController().hideBackdrop();
    }

    const handleReqFetchConfirmed = async () => {
        const timestamp = Date.now().toString();
        const signature = SellertoolSignatureUtils().generateSigniture({
            apiKey: apiInfoContextValueHook?.apiInfo?.apiKey,
            secretKey: apiInfoContextValueHook?.apiInfo?.secretKey,
            timestamp: timestamp
        })

        const headers = {
            'x-sellertool-apiKey': apiInfoContextValueHook?.apiInfo?.apiKey,
            'x-sellertool-timestamp': timestamp,
            'x-sellertool-signiture': signature
        }

        const params = {
            stageUUIDs: [currentStageUUID]
        }

        const fetchResult = await axios.get(`${API_HOST}/confirmed-pallets`, {
            params: params,
            headers: headers,
            paramsSerializer: params => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            }
        })
            .then(res => {
                return {
                    res: res,
                    content: res.data?.content
                }
            })
            .catch(err => {
                customToast.error(err.response);
            })

        if (fetchResult?.content) {
            if (fetchResult?.content?.pallets?.length <= 0) {
                setConfirmedPallets(null);
                setConfirmedLoadItems(null);
            } else {
                setConfirmedPallets(fetchResult?.content?.pallets);
                setConfirmedLoadItems(fetchResult?.content?.loadItems);
            }
        }
    }

    // 준비 → 확정 이동 후 양쪽 데이터를 함께 갱신
    const handleReqFetchAll = async () => {
        await Promise.all([handleReqFetch(), handleReqFetchConfirmed()]);
    }

    const handleFetchCurrentStage = async () => {
        const timestamp = Date.now().toString();
        const signature = SellertoolSignatureUtils().generateSigniture({
            apiKey: apiInfoContextValueHook?.apiInfo?.apiKey,
            secretKey: apiInfoContextValueHook?.apiInfo?.secretKey,
            timestamp: timestamp
        });

        const headers = {
            'x-sellertool-apiKey': apiInfoContextValueHook?.apiInfo?.apiKey,
            'x-sellertool-timestamp': timestamp,
            'x-sellertool-signiture': signature
        };

        axios.get(`${API_HOST}/inbound-stages/uuid/${currentStageUUID}`, {
            headers: headers
        })
            .then(res => {
                if (res.status === 200 && res.data?.content) {
                    setCurrentStage(res.data.content);
                }
            })
            .catch(err => {
                customToast.error(err.response);
            });
    }

    const handleReqBulkAdd = async (pallets, loadItems) => {
        const timestamp = Date.now().toString();
        const signature = SellertoolSignatureUtils().generateSigniture({
            apiKey: apiInfoContextValueHook?.apiInfo?.apiKey,
            secretKey: apiInfoContextValueHook?.apiInfo?.secretKey,
            timestamp: timestamp
        })

        const headers = {
            'x-sellertool-apiKey': apiInfoContextValueHook?.apiInfo?.apiKey,
            'x-sellertool-timestamp': timestamp,
            'x-sellertool-signiture': signature
        }

        const body = {
            pallets: pallets,
            loadItems: loadItems
        }

        CustomBackdropController().showBackdrop();
        const bulkAddResult = await axios.post(`${API_HOST}/pallets/bulk-add`, body, {
            headers: headers,
        })
            .then(res => {
                return {
                    res: res,
                    content: res.data?.content
                }
            })
            .catch(err => {
                customToast.error(err.response);
            })

        if (bulkAddResult?.content) {
            setPallets(bulkAddResult?.content?.pallets);
            setLoadItems(bulkAddResult?.content?.loadItems);
        }
        CustomBackdropController().hideBackdrop();
    }

    const handleReqBulkDelete = async () => {
        const timestamp = Date.now().toString();
        const signature = SellertoolSignatureUtils().generateSigniture({
            apiKey: apiInfoContextValueHook?.apiInfo?.apiKey,
            secretKey: apiInfoContextValueHook?.apiInfo?.secretKey,
            timestamp: timestamp
        })

        const headers = {
            'x-sellertool-apiKey': apiInfoContextValueHook?.apiInfo?.apiKey,
            'x-sellertool-timestamp': timestamp,
            'x-sellertool-signiture': signature
        }

        const data = {
            stageUUID: currentStage?.uuid
        }

        CustomBackdropController().showBackdrop();
        const bulkAddResult = await axios.delete(`${API_HOST}/pallets/bulk-delete`, {
            headers: headers,
            data: data,
        })
            .then(res => {
                return {
                    res: res,
                    content: res.data?.content
                }
            })
            .catch(err => {
                customToast.error(err.response);
            })

        if (bulkAddResult?.content) {
            setPallets(null);
            setLoadItems(null);
        }
        CustomBackdropController().hideBackdrop();
    }

    return (
        <>
            <St.PageHeader>
                <div className='stageSlot'>
                    <WegetStageSelection
                        currentStage={currentStage}
                    />
                </div>
                <St.StandardNavButton
                    type='button'
                    className={page === 'STANDARD' ? 'active' : ''}
                    onClick={() => setPage(prev => prev === 'STANDARD' ? 'MAIN' : 'STANDARD')}
                >
                    {page === 'STANDARD' ? '◀ 적재 화면' : '규격 적재'}
                </St.StandardNavButton>
            </St.PageHeader>

            {page === 'STANDARD'
                ? <WegetStandardRules />
                : currentStage &&
                    <>
                        <WegetTabs
                            currentTab={tab}
                            onSetTab={handleSetTab}
                        />
                        {tab === 'LOAD' &&
                            <WegetEventButtons
                                pallets={pallets}
                                loadItems={loadItems}
                                stageUUID={currentStage?.uuid}
                                onReqBulkAdd={handleReqBulkAdd}
                                onReqBulkDelete={handleReqBulkDelete}
                            />
                        }
                        {tab === 'PALLETS' &&
                            <WegetViewerPalletFirst
                                pallets={pallets}
                                loadItems={loadItems}
                                stageUUID={currentStage?.uuid}
                                currentStage={currentStage}
                                reloadCurrentStage={handleFetchCurrentStage}
                                onReqFetch={handleReqFetch}
                                apiResource='pallets'
                                mode='PREPARE'
                                counterpartPallets={confirmedPallets}
                                onReqFetchAll={handleReqFetchAll}
                            />
                        }
                        {tab === 'CONFIRM' &&
                            <WegetViewerPalletFirst
                                pallets={confirmedPallets}
                                loadItems={confirmedLoadItems}
                                stageUUID={currentStage?.uuid}
                                currentStage={currentStage}
                                reloadCurrentStage={handleFetchCurrentStage}
                                onReqFetch={handleReqFetchConfirmed}
                                apiResource='confirmed-pallets'
                                mode='CONFIRM'
                                counterpartPallets={pallets}
                                onReqFetchAll={handleReqFetchAll}
                            />
                        }
                        {tab === 'ITEMS' &&
                            <WegetViewerItemFirst
                                pallets={pallets}
                                loadItems={loadItems}
                                stageUUID={currentStage?.uuid}
                                onReqFetch={handleReqFetch}
                                onReqFetchAll={handleReqFetchAll}
                                confirmedPallets={confirmedPallets}
                                apiResource='pallets'
                            />
                        }
                    </>
            }
            {/* <WegetPalletDatas
                palletDatas={palletDatas}
            /> */}

            <ScrollToTopButton />
        </>
    );
}